import { type fabric } from 'fabric';
import GuideSpace from './GuideSpace';
import { type Constructor } from '../types';
import {
  hasFrameBehaviors,
  hasSmartGuideBehaviors,
  isDevice,
  isEmbeddedBookmark,
  isFrame,
  hasLayout,
  isShape,
  isSimpleGroup,
  isSimpleSelection,
  isTable,
  isWireframe,
  hasFrameMemberBehaviors,
} from './guards';
import { type Transform } from 'fabric/fabric-impl';
import GuideAlign from './GuideAlign';

// Proximity necessary for guide to show.
const SNAP_DISTANCE = 8;

// How much the guides should go beyond each objects.
const GUIDE_OVERLAP = 20;

// Maximum number of objects to consider snapping to.
const MAX_OBJECTS_TO_CONSIDER = 100;

// Opacity of guides when shown.
const GUIDE_LINE_OPACITY = 0.3;
const GUIDE_SPACE_OPACITY = 0.4;

type Side =
  | 'bottom'
  | 'top'
  | 'left'
  | 'right'
  | 'spaceH'
  | 'spaceV'
  | 'centerY'
  | 'centerX';
interface Line {
  from: unknown;
  to: unknown;
}
type Axis = 'h' | 'v';

interface BaseRuler {
  side: Side;
  axis: Axis;
  pos: number;
  matchOffset?: number;
}
interface AlignRuler extends BaseRuler {
  kind: 'align';
  origin: number;
  min: number;
  max: number;
}

interface InternalSmartGuideBehaviors {
  minOffAxisSide(axis: Axis): number;
  maxOffAxisSide(axis: Axis): number;
  minAxisSide(axis: Axis): number;
  maxAxisSide(axis: Axis): number;
  axisOverlaps(axis: Axis, object: InternalSmartGuideBehaviors): boolean;
}
interface DistributeRuler extends BaseRuler {
  kind: 'distributeMid' | 'distributeEdgeMin' | 'distributeEdgeMax';
  objects: [InternalSmartGuideBehaviors, InternalSmartGuideBehaviors];
  lines?: Line[];
}
type Ruler = AlignRuler | DistributeRuler;

interface BoundingBox {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
}
interface Rulers {
  h: Ruler[];
  v: Ruler[];
}
interface GuideState {
  rulers: Rulers;
  boundingBox: BoundingBox;
  moving?: boolean;
}
type Guides = Record<string, fabric.Object>;
interface ControlPoint {
  x: number;
  y: number;
}

// Behaviours added onto objects that support smart guides and snapping.
interface SmartGuideBehaviors {
  initializeSmartGuides(): void;
  computeGuides(controlPoint: ControlPoint | null, axes: Axis[]): void;
  hideGuides(): void;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function SmartGuideBehaviors(Base: Constructor<fabric.Object>): any {
  return class extends Base implements SmartGuideBehaviors {
    guides?: Guides | null;
    guideState: GuideState;

    disableSmartGuides: boolean = false;

    initializeSmartGuides() {
      // The guides for the object, populated at the start of a drag operation
      // and cleared at the end. As the object moves the guides are moved with
      // it.
      this.guides = null;

      this.on('moving', e => {
        // If a frame is moving then objects in the frame ignore guides.
        if (e.frameMoving && this.id !== e.frameMoving) return;
        // Suppress guides when Ctrl/cmd key is held down.
        if (e.e?.ctrlKey || e.e?.metaKey) {
          this.removeGuides();
          return;
        }

        this.prepareGuideState();
        this.computeGuides();
      });
      this.on('scaling', () => this.prepareGuideState());
      this.on('moved', () => this.removeGuides());
      this.on('scaled', () => this.removeGuides());
    }

    prepareGuideState() {
      if (this.guides) return;
      if (!this.supportsGuides()) return;

      this.createGuides();

      // Compute the rulers for all objects in range. Rulers are vertical or
      // horizontal lines that the object might align with. We have two kinds
      // of rulers:
      //
      //    1. 'align' rulers representing an alignment between the objects.
      //    2. 'distribute' rulers representing the center of a distribution between objects.
      this.guideState = {
        rulers: { h: [], v: [] },
        boundingBox: this.getBoundingBox(true),
      };

      this.collectAllRulers();
    }

    supportsGuides() {
      if (this.disableSmartGuides) return false;

      // Smart guides don't appear on grouped objects, only on the group itself,
      // unless the object is the current selection because the user double
      // clicked into the group.
      const selected = this.canvas?.getActiveObject() === this;
      if ((this.simpleGroup && !selected) || !this.canvas) return false;

      // Labels should not have smart guides
      if (this.connectorId) return false;

      // Objects inside Layout containers should not have smart guides.
      if (hasFrameMemberBehaviors(this) && this.container) {
        const frame = this.inFrame();
        if (frame && hasLayout(frame)) return false;
      }

      return true;
    }

    computeGuides(
      controlPoint: ControlPoint | null = null,
      axes: Axis[] = ['h', 'v']
    ) {
      this.hideGuides();

      if (!this.supportsGuides()) return;

      this.prepareGuideState();

      //  This object is moving, so recompute its bounding box.
      this.prepareBoundingBox(true);

      this.guideState.moving = !controlPoint;

      // Describe the current object by its sides.
      const thisSides = this.collectSides(this);

      const zoom = this.canvas.getZoom();

      // First we find the best match to snap to. Then after snapping we find
      // all of the (almost) exact matches to show guides for. We do this
      // separately in each direction.
      for (const axis of axes) {
        let alignMatches: AlignRuler[];
        let distributeMatches: DistributeRuler[] = [];

        if (!controlPoint) {
          alignMatches = this.findAlignMatchesForSides(
            axis,
            thisSides,
            SNAP_DISTANCE / zoom
          );
          distributeMatches = this.findDistributeMatchesForSides(
            axis,
            SNAP_DISTANCE / zoom
          );
        } else {
          alignMatches = this.findAlignMatchesForPoint(
            axis,
            controlPoint,
            SNAP_DISTANCE / zoom
          );
        }

        alignMatches = this.filterMatches(alignMatches);

        if (alignMatches.length > 0 || distributeMatches.length > 0) {
          if (!controlPoint) {
            this.snapToMatchesForSides(axis, [
              ...alignMatches,
              ...distributeMatches,
            ]);
          } else {
            this.snapToMatchesForPoint(axis, controlPoint, [
              ...alignMatches,
              ...distributeMatches,
            ]);
          }

          this.prepareBoundingBox(true);

          if (!controlPoint) {
            // Recompute the sides since the object may have moved while snapping.
            const thisSides = this.collectSides(this);
            alignMatches = this.findAlignMatchesForSides(axis, thisSides, 1);
            distributeMatches = this.findDistributeMatchesForSides(axis, 1);
          } else {
            alignMatches = this.findAlignMatchesForPoint(axis, controlPoint, 1);
          }

          alignMatches = this.filterMatches(alignMatches);

          this.showAlignGuides(alignMatches);
          this.showDistributeGuides(distributeMatches);
        }
      }
    }

    hideGuides() {
      // Hide all the guides.
      for (const side in this.guides) {
        this.guides[side].set('opacity', 0);
      }
    }

    createGuides() {
      if (this.guides) return;

      this.guides = {};
      const w = this.getScaledWidth();
      const h = this.getScaledHeight();
      this.createGuide('top', this.top);
      this.createGuide('left', this.left);
      this.createGuide('centerX', this.left + w / 2);
      this.createGuide('centerY', this.top + h / 2);
      this.createGuide('right', this.left + w);
      this.createGuide('bottom', this.top + h);

      this.createGuide('spaceH', this.top + h / 2);
      this.createGuide('spaceV', this.left + w / 2);

      this.canvas.addObjects(Object.values(this.guides), { where: 'top' });
    }

    removeGuides() {
      for (const guide in this.guides) {
        this.canvas.remove(this.guides[guide]);
      }
      delete this.guides;
    }

    createGuide(side: Side, pos: number) {
      let top = 0;
      let left = 0;
      let width = 0;
      let height = 0;

      switch (side) {
        case 'top':
        case 'bottom':
        case 'centerY':
        case 'spaceH':
          top = pos;
          width = this.canvas.width as number;
          break;

        case 'left':
        case 'right':
        case 'centerX':
        case 'spaceV':
          left = pos;
          height = this.canvas.height as number;
          break;
      }

      const GuideClass =
        side === 'spaceH' || side === 'spaceV' ? GuideSpace : GuideAlign;

      const ln = new GuideClass([0, 0, width, height], {
        left,
        top,
        evented: false,
        stroke: '#4273fb',
        selectable: false,
        objectCaching: false,
        noScaleCache: false,
        opacity: 0,
      });

      (this.guides as Guides)[side] = ln;
    }

    prepareBoundingBox(calculate = false) {
      this.guideState = {
        ...this.guideState,
        boundingBox: this.getBoundingBox(calculate),
      };
    }

    getBoundingBox(calculate = false): BoundingBox {
      const r = this.getBoundingRect(true, calculate);
      return {
        left: r.left,
        top: r.top,
        width: r.width,
        height: r.height,
        right: r.left + r.width,
        bottom: r.top + r.height,
      };
    }

    collectAllRulers() {
      let consideredObjectCount = 0;
      const distributeRulerCandidates: {
        h: InternalSmartGuideBehaviors[];
        v: InternalSmartGuideBehaviors[];
      } = { h: [], v: [] };

      // Find objects that this object can align with.
      this.canvas
        .getObjects()
        .filter(
          (o): o is this =>
            (isShape(o) ||
              isSimpleGroup(o) ||
              isFrame(o) ||
              isTable(o) ||
              isWireframe(o) ||
              isEmbeddedBookmark(o) ||
              isDevice(o)) && // Only shapes, groups, frames, wireframes, and devices can be aligned
            !(isFrame(this) && o.isDescendant(this)) && // Frames don't align with their contents
            // @ts-expect-error since `this` is a decorator class, it may be any object subclass
            o !== this && // Don't align against ourself
            o !== this.simpleGroup && // Don't align against our parent for grouped objects
            o !== this.simpleGroup?.simpleGroup && // And our parent's parent. This doesn't handle deeeply nested groups, but that would be more expensive, and unlikely
            // @ts-expect-error since `this` is a decorator class, it may in fact be a group or selection
            o.simpleGroup !== this && // Don't align against our children for the active selection
            o.isOnScreen(false) && // Only align against objects on the screen
            !o.connectorId // Don't align against labels
        )
        .forEach(obj => {
          // Make sure we don't make things too slow when too many objects are visible at once.
          if (consideredObjectCount++ > MAX_OBJECTS_TO_CONSIDER) return;

          // Compute the bounding box for each object we might align to.
          obj.prepareBoundingBox();

          this.guideState.rulers.h.push(...this.collectObjectHSideRulers(obj));
          this.guideState.rulers.v.push(...this.collectObjectVSideRulers(obj));

          // Collect objects for distribute rulers.
          distributeRulerCandidates.h.push(obj);
          distributeRulerCandidates.v.push(obj);
        });

      // Generate the distribute rulers
      for (const axis of ['h', 'v'] as const) {
        for (let i = 0; i < distributeRulerCandidates[axis].length; i++) {
          const fromObj = distributeRulerCandidates[axis][i];
          for (let j = i + 1; j < distributeRulerCandidates[axis].length; j++) {
            const toObj = distributeRulerCandidates[axis][j];
            const sides = [
              fromObj.minAxisSide(axis),
              fromObj.maxAxisSide(axis),
              toObj.minAxisSide(axis),
              toObj.maxAxisSide(axis),
            ].sort((a, b) => a - b);

            // Ruler at the midpoint between two objects - will align with
            // midpoint of moving object.
            this.guideState.rulers[axis].push({
              kind: 'distributeMid',
              side: axis === 'h' ? 'spaceH' : 'spaceV',
              axis,
              pos: (sides[1] + sides[2]) / 2,
              objects: [fromObj, toObj],
            });

            // Ruler at equal space beyond each object - will align with side
            // of moving object.
            const spaceBetween = sides[2] - sides[1];
            this.guideState.rulers[axis].push({
              kind: 'distributeEdgeMin',
              side: axis === 'h' ? 'spaceH' : 'spaceV',
              axis,
              pos: sides[0] - spaceBetween,
              objects: [fromObj, toObj],
            });
            this.guideState.rulers[axis].push({
              kind: 'distributeEdgeMax',
              side: axis === 'h' ? 'spaceH' : 'spaceV',
              axis,
              pos: sides[3] + spaceBetween,
              objects: [fromObj, toObj],
            });
          }
        }
      }
    }

    collectSides(obj: this) {
      const sides: AlignRuler[] = [];
      sides.push(...this.collectObjectHSideRulers(obj));
      sides.push(...this.collectObjectVSideRulers(obj));
      return sides;
    }

    collectObjectHSideRulers(obj: this): AlignRuler[] {
      const kind = 'align';
      const bb = obj.guideState.boundingBox;
      const min = bb.left;
      const max = bb.right;

      return [
        { kind, side: 'top', axis: 'h', pos: bb.top, origin: bb.top, min, max },
        {
          kind,
          side: 'centerY',
          axis: 'h',
          pos: bb.top + bb.height / 2,
          origin: bb.top,
          min,
          max,
        },
        {
          kind,
          side: 'bottom',
          axis: 'h',
          pos: bb.bottom,
          origin: bb.top,
          min,
          max,
        },
      ];
    }

    collectObjectVSideRulers(obj: this): AlignRuler[] {
      const kind = 'align';
      const bb = obj.guideState.boundingBox;
      const min = bb.top;
      const max = bb.bottom;

      return [
        {
          kind,
          side: 'left',
          axis: 'v',
          pos: bb.left,
          origin: bb.left,
          min,
          max,
        },
        {
          kind,
          side: 'centerX',
          axis: 'v',
          pos: bb.left + bb.width / 2,
          origin: bb.left,
          min,
          max,
        },
        {
          kind,
          side: 'right',
          axis: 'v',
          pos: bb.right,
          origin: bb.left,
          min,
          max,
        },
      ];
    }

    findAlignMatchesForSides(
      axis: Axis,
      thisSides: AlignRuler[],
      margin: number
    ) {
      const matches: AlignRuler[] = [];

      for (const ruler of this.guideState.rulers[axis]) {
        if (ruler.kind !== 'align') continue;

        for (const side of thisSides) {
          if (side.axis !== axis) continue;

          if (Math.abs(ruler.pos - side.pos) <= margin) {
            // We need to compute the relative offset from the matching sides to
            // the top/left of this object.
            ruler.matchOffset = ruler.pos - (side.pos - side.origin);
            matches.push(ruler);
          }
        }
      }

      return matches;
    }

    findAlignMatchesForPoint(
      axis: Axis,
      controlPoint: ControlPoint,
      margin: number
    ) {
      const matches: AlignRuler[] = [];

      for (const ruler of this.guideState.rulers[axis]) {
        if (ruler.kind !== 'align') continue;

        if (
          Math.abs(ruler.pos - controlPoint[axis === 'h' ? 'y' : 'x']) <= margin
        ) {
          matches.push(ruler);
        }
      }

      return matches;
    }

    findDistributeMatchesForSides(axis: Axis, margin: number) {
      const matches: DistributeRuler[] = [];

      const thisMidpoint = this.axisMidPoint(axis);
      const thisMinSide = this.minAxisSide(axis);
      const thisMaxSide = this.maxAxisSide(axis);

      for (const ruler of this.guideState.rulers[axis]) {
        // Only consider distribute rulers
        if (
          ruler.kind !== 'distributeMid' &&
          ruler.kind !== 'distributeEdgeMin' &&
          ruler.kind !== 'distributeEdgeMax'
        ) {
          continue;
        }

        if (
          !this.axisOverlaps(axis === 'h' ? 'v' : 'h', ruler.objects[0]) ||
          !this.axisOverlaps(axis === 'h' ? 'v' : 'h', ruler.objects[1])
        ) {
          continue;
        }

        // Ignore objects that overlap each other or overlap the object
        // being dragged.
        const [fromObj, toObj] = ruler.objects;
        if (fromObj.axisOverlaps(axis, toObj)) continue;
        if (this.axisOverlaps(axis, toObj)) continue;
        if (this.axisOverlaps(axis, fromObj)) continue;

        const oppositeAxis = axis === 'h' ? 'v' : 'h';
        const fromMin = fromObj.minAxisSide(oppositeAxis);
        const fromMax = fromObj.maxAxisSide(oppositeAxis);
        const toMin = toObj.minAxisSide(oppositeAxis);
        const toMax = toObj.maxAxisSide(oppositeAxis);
        const thisMin = this.minAxisSide(oppositeAxis);
        const thisMax = this.maxAxisSide(oppositeAxis);

        // Ignore objects where there is not some overlap between all three
        // objects.
        if (fromMin > toMax || fromMin > thisMax) continue;
        if (fromMax < toMin || fromMax < thisMin) continue;
        if (toMin > fromMax || toMin > thisMax) continue;
        if (toMax < fromMin || toMax < thisMin) continue;

        if (
          ruler.kind === 'distributeMid' &&
          Math.abs(ruler.pos - thisMidpoint) <= margin
        ) {
          ruler.matchOffset = ruler.pos - this.axisSize(axis) / 2;
          matches.push(ruler);
        }
        if (
          ruler.kind === 'distributeEdgeMin' &&
          Math.abs(ruler.pos - thisMaxSide) <= margin
        ) {
          ruler.matchOffset = ruler.pos - this.axisSize(axis);
          matches.push(ruler);
        }
        if (
          ruler.kind === 'distributeEdgeMax' &&
          Math.abs(ruler.pos - thisMinSide) <= margin
        ) {
          ruler.matchOffset = ruler.pos;
          matches.push(ruler);
        }
      }

      // Save the bounds of the space lines that this ruler would generate.
      for (const ruler of matches) {
        ruler.lines = [];

        const lineObjects = [ruler.objects[0], ruler.objects[1], this].sort(
          (a, b) => a.minAxisSide(axis) - b.minAxisSide(axis)
        );

        ruler.lines.push({
          from: lineObjects[0].maxAxisSide(axis),
          to: lineObjects[1].minAxisSide(axis),
        });
        ruler.lines.push({
          from: lineObjects[1].maxAxisSide(axis),
          to: lineObjects[2].minAxisSide(axis),
        });
      }

      return matches;
    }

    // Prefer matches which are between closer objects and matches that
    // relate to the center of the object.
    rankMatches<T extends Ruler>(matches: T[]): T[] {
      const rankSides = (a: T) => {
        switch (a.side) {
          case 'centerX':
          case 'centerY':
            return 5;
          case 'left':
          case 'top':
            return 4;
          case 'right':
          case 'bottom':
            return 3;
          case 'spaceH':
          case 'spaceV':
            return 2;
          default:
            throw new Error('Unknown side');
        }
      };

      const matchDistance = (match: T) => {
        if (match.kind !== 'align') {
          return NaN;
        }

        const bb = this.guideState.boundingBox;

        // Find the distance between the nearest sides of each object. We don't
        // know which way around the objects are (which is to the left and
        // which is to the right), but the minimum distance is all we care about.
        if (match.axis === 'h') {
          return Math.min(
            Math.abs(match.max - bb.left),
            Math.abs(match.min - bb.right)
          );
        } else {
          return Math.min(
            Math.abs(match.max - bb.top),
            Math.abs(match.min - bb.bottom)
          );
        }
      };

      return matches.sort(
        (a, b) =>
          matchDistance(a) - matchDistance(b) || rankSides(b) - rankSides(a)
      );
    }

    snapToMatchesForSides(axis: Axis, matches: Ruler[]) {
      // Rank the matches.
      const rankedMatches = this.rankMatches(matches);
      const match = rankedMatches.find(match => match.axis === axis);
      if (match) {
        const axisOrigin = axis === 'v' ? 'left' : 'top';
        this.set(
          axisOrigin,
          (match.matchOffset as number) +
            this[axisOrigin] -
            this.guideState.boundingBox[axisOrigin]
        );
        this.setCoords();

        // Make sure that groups move their contained objects in sync with the
        // snap. We are already in a 'move' callback so we can't fire that
        // again.
        if (isSimpleGroup(this) || isSimpleSelection(this)) {
          this.transformObjects('moving');
        }

        // Frames move their contents too.
        if (hasFrameBehaviors(this) && this.frameBehaviorsEnabled) {
          this.moveWithContents();
        }
      }
    }

    snapToMatchesForPoint(
      axis: Axis,
      controlPoint: ControlPoint,
      matches: Ruler[]
    ) {
      // Rank the matches.
      const rankedMatches = this.rankMatches(matches);
      const match = rankedMatches.find(match => match.axis === axis);
      if (match) {
        controlPoint[axis === 'h' ? 'y' : 'x'] = match.pos;
      }
    }

    showDistributeGuides(matches: DistributeRuler[]) {
      for (const match of matches) {
        this.showDistributeGuide(match);
      }
    }

    // Only show a single match for each axis and match type. This prevents
    // us showing center and side guides at the same time for identically
    // sized objects.
    filterMatches<T extends Ruler>(matches: T[]): T[] {
      const filteredMatches: T[] = [];
      const seen: Record<string, boolean> = {};
      const rankedMatches = this.rankMatches(matches);

      for (const match of rankedMatches) {
        const key = `${match.axis}-${match.kind}`;
        if (!seen[key]) {
          filteredMatches.push(match);
          seen[key] = true;
        }
      }

      return filteredMatches;
    }

    showAlignGuides(matches: AlignRuler[]) {
      const matchExtents: Record<string, Omit<AlignRuler, 'origin'>> = {};

      for (const match of matches) {
        matchExtents[match.side] ||= {
          kind: 'align',
          side: match.side,
          axis: match.axis,
          pos: match.pos,
          min: match.min,
          max: match.max,
        };
        matchExtents[match.side].min = Math.min(
          match.min,
          matchExtents[match.side].min
        );
        matchExtents[match.side].max = Math.max(
          match.max,
          matchExtents[match.side].max
        );
      }

      for (const match of Object.values(matchExtents)) {
        this.showAlignGuide(match);
      }
    }

    axisOverlaps(axis: Axis, object: InternalSmartGuideBehaviors) {
      const objMin = object.minAxisSide(axis);
      const objMax = object.maxAxisSide(axis);
      const thisMin = this.minAxisSide(axis);
      const thisMax = this.maxAxisSide(axis);

      return (
        (thisMin >= objMin && thisMin <= objMax) ||
        (thisMax >= objMin && thisMax <= objMax) ||
        (thisMin <= objMin && thisMax >= objMax)
      );
    }

    axisMidPoint(axis: Axis) {
      const bb = this.guideState.boundingBox;
      if (axis === 'v') {
        return bb.left + bb.width / 2;
      } else {
        return bb.top + bb.height / 2;
      }
    }

    axisSize(axis: Axis) {
      const bb = this.guideState.boundingBox;
      if (axis === 'h') {
        return bb.height;
      } else {
        return bb.width;
      }
    }

    minAxisSide(axis: Axis) {
      const bb = this.guideState.boundingBox;
      if (axis === 'h') {
        return bb.top;
      } else {
        return bb.left;
      }
    }

    maxAxisSide(axis: Axis) {
      const bb = this.guideState.boundingBox;
      if (axis === 'h') {
        return bb.bottom;
      } else {
        return bb.right;
      }
    }

    minOffAxisSide(axis: Axis) {
      const bb = this.guideState.boundingBox;
      if (axis === 'v') {
        return bb.top;
      } else {
        return bb.left;
      }
    }

    maxOffAxisSide(axis: Axis) {
      const bb = this.guideState.boundingBox;
      if (axis === 'v') {
        return bb.bottom;
      } else {
        return bb.right;
      }
    }

    showDistributeGuide(match: DistributeRuler) {
      const zoom = this.canvas.getZoom();
      const minDimension = Math.max(
        this.minOffAxisSide(match.axis),
        match.objects[0].minOffAxisSide(match.axis),
        match.objects[1].minOffAxisSide(match.axis)
      );
      const maxDimension = Math.min(
        this.maxOffAxisSide(match.axis),
        match.objects[0].maxOffAxisSide(match.axis),
        match.objects[1].maxOffAxisSide(match.axis)
      );

      const config: Record<string, unknown> = {
        opacity: GUIDE_SPACE_OPACITY,
        strokeWidth: 1 / zoom,
        lines: match.lines,
        axis: match.axis,
      };

      if (match.axis === 'v') {
        config.top = minDimension;
        config.height = maxDimension - minDimension;
      } else {
        config.left = minDimension;
        config.width = maxDimension - minDimension;
      }

      (this.guides as Guides)[match.side].set(config);
      (this.guides as Guides)[match.side].setCoords();
    }

    showAlignGuide(matchExtent: Omit<AlignRuler, 'origin'>) {
      const zoom = this.canvas.getZoom();
      const overlap = GUIDE_OVERLAP / zoom;
      const bb = this.guideState.boundingBox;

      const config: Record<string, unknown> = {
        opacity: GUIDE_LINE_OPACITY,
        strokeWidth: 1 / zoom,
      };
      if (matchExtent.axis === 'h') {
        matchExtent.min = Math.min(matchExtent.min, bb.left);
        matchExtent.max = Math.max(matchExtent.max, bb.right);
        config.top = matchExtent.pos;
        config.left = matchExtent.min - overlap;
        config.width = 2 * overlap + matchExtent.max - matchExtent.min;
      } else {
        matchExtent.min = Math.min(matchExtent.min, bb.top);
        matchExtent.max = Math.max(matchExtent.max, bb.bottom);
        config.left = matchExtent.pos;
        config.top = matchExtent.min - overlap;
        config.height = 2 * overlap + matchExtent.max - matchExtent.min;
      }

      (this.guides as Guides)[matchExtent.side].set(config);
      (this.guides as Guides)[matchExtent.side].setCoords();
    }
  };
}

type ActionHandler = (
  eventData: MouseEvent,
  transform: Transform,
  x: number,
  y: number
) => boolean;

function resizeWithSmartGuides(
  actionHandler: ActionHandler,
  axes: Axis[] = ['h', 'v']
): ActionHandler {
  return function (eventData, transform, x, y) {
    const transformPoint = { x, y };

    // This approach of moving the x/y point does not work for uniform
    // scaling objects so we just ignore the guides.
    //
    // Ctrl key suppresses guides.
    if (
      (!transform.target.scaleIsProportional(eventData) || axes.length === 1) &&
      !eventData.ctrlKey &&
      !eventData.metaKey &&
      hasSmartGuideBehaviors(transform.target)
    ) {
      transform.target.computeGuides(transformPoint, axes);
    } else if (hasSmartGuideBehaviors(transform.target)) {
      // Hide guides in case they are being enabled/disabled during an
      // operation.
      transform.target.hideGuides();
    }

    return actionHandler(
      eventData,
      transform,
      transformPoint.x,
      transformPoint.y
    );
  };
}

export default SmartGuideBehaviors;
export { resizeWithSmartGuides };
